import { Validator } from 'redux-form';
import memoizee from 'memoizee';
import moment from 'moment';

import { i18n } from 'app/i18n';
import { I18nUtils } from 'app/utils/i18n-utils';
import { isEmail, isDateOlder, isNonEmptyString, isNumeric } from 'app/validation/checks';

const MINIMUM_PHONE_NUMBER_LENGTH = 4;
const MAXIMUM_PHONE_NUMBER_LENGTH = 15;

type ValidationType = string | undefined;

const required = memoizee(
    (message: string = i18n.validation.field.required): Validator =>
        (value: any): ValidationType => {
            if (!value) {
                return message;
            }

            if (typeof value === 'string') {
                return whiteSpaceValidator(value);
            }
        },
);

const minLength = memoizee(
    (length: number, message?: string): Validator =>
        (value: string): ValidationType => {
            if (value && value.trim().length < length) {
                return (
                    message ||
                    I18nUtils.message(i18n.validation.input.lengthTooShort, {
                        length: length.toString(),
                    })
                );
            }

            return undefined;
        },
);

const maxLength = memoizee(
    (length: number, message?: string): Validator =>
        (value: string): ValidationType => {
            if (value && value.trim().length > length) {
                return (
                    message ||
                    I18nUtils.message(i18n.validation.input.lengthTooLong, {
                        length: length.toString(),
                    })
                );
            }

            return undefined;
        },
);

const lengthWithoutWhitespaces = memoizee(
    (minLengthWithoutWhitespaces: number = 1, message?: string): Validator =>
        (value: string): ValidationType => {
            if (value) {
                const trimmedValue = value.trim();

                if (!isNonEmptyString(trimmedValue) || minLength(minLengthWithoutWhitespaces)(trimmedValue)) {
                    return message || i18n.validation.value.notValid;
                }
            }

            return undefined;
        },
);

const whiteSpaceValidator = lengthWithoutWhitespaces();

const stringOnlyWithDigitsValidation = memoizee(
    (message?: string): Validator =>
        (value: string): ValidationType => {
            if (value && !isNumeric(value)) {
                return message || i18n.validation.input.onlyDigits;
            }
            return undefined;
        },
);

const phoneNumberValidation = memoizee(
    (message?: string): Validator =>
        (value: string): ValidationType => {
            if (value) {
                if (!isNumeric(value)) {
                    return i18n.validation.phoneNumber.onlyDigits;
                }
                if (value.length < MINIMUM_PHONE_NUMBER_LENGTH) {
                    return (
                        message ||
                        I18nUtils.message(i18n.validation.input.lengthTooShort, {
                            length: MINIMUM_PHONE_NUMBER_LENGTH.toString(),
                        })
                    );
                } else if (value.length > MAXIMUM_PHONE_NUMBER_LENGTH) {
                    return (
                        message ||
                        I18nUtils.message(i18n.validation.input.lengthTooLong, {
                            length: MAXIMUM_PHONE_NUMBER_LENGTH.toString(),
                        })
                    );
                }
            }

            return undefined;
        },
);

const phoneNumberWithCountryCallCodeValidation = memoizee(
    (message?: string): Validator =>
        (value: string): ValidationType => {
            if (value) {
                return phoneNumberValidation(message)(value.replace('+', ''));
            }

            return undefined;
        },
);

const noWhiteSpacesValidation = memoizee(
    (message?: string): Validator =>
        (value: string): ValidationType => {
            if (value && !/^\S*$/.test(value)) {
                return message || i18n.validation.input.noWhitespaces;
            }

            return undefined;
        },
);

const countryCallCodeValidation = memoizee(
    (message?: string): Validator =>
        (value: string): ValidationType => {
            if (value && !/^\+\d{1,3}$/.test(value)) {
                return message || i18n.validation.countryCode.invalid;
            }

            return undefined;
        },
);

const emailValidation = memoizee(
    (message?: string): Validator =>
        (value: string): ValidationType => {
            if (!isEmail(value)) {
                return message || i18n.validation.email.notValid;
            }

            return undefined;
        },
);

const dateOlderThanValidation = memoizee(
    (duration: moment.Duration, message?: string): Validator =>
        (value: string): ValidationType => {
            if (isDateOlder(moment(value), duration)) {
                return message || i18n.validation.date.tooOld;
            }

            return undefined;
        },
);

const validators = {
    required,
    minLength,
    maxLength,
    countryCallCodeValidation,
    phoneNumberValidation,
    phoneNumberWithCountryCallCodeValidation,
    noWhiteSpacesValidation,
    stringOnlyWithDigitsValidation,
    lengthWithoutWhitespaces,
    emailValidation,
    dateOlderThanValidation,
};

export { validators };
